import { Icon, Switch } from '@distribusion/owl-frontend-ui'
import { getIn, useFormikContext } from 'formik'
import React, { ReactElement, useCallback, useMemo, useState } from 'react'

import Loader from '@components/Loader'
import seat from '@images/extras/seat.png'
import ancillaryUtils from '@lib/ancillary'
import bem from '@lib/bem'
import { useTranslation } from '@lib/i18n'
import seatsUtils from '@lib/seatSelection'
import Card from '@pages/Checkout/Extras/Card'
import { CheckoutFormData } from '@pages/Checkout/hooks/useInitialFormValues'
import Info from '@pages/Checkout/SeatSelection/Seats/Info'

interface PreviewProps {
  onOpenModal: () => boolean
  outbound: Connection | null
  inbound: Connection | null
  hasSeatMaps: boolean
  disabled?: boolean
  ancillary?: Ancillary.Item
  seatsLoading?: boolean
}

const Warning = ({ title }: { title: string }): ReactElement => (
  <div className="extras__seats-warning-message seats-error row gap-1">
    <Icon name="alert" size="small" />
    <span>{title}</span>
  </div>
)

const Control = (): ReactElement => (
  <div className={bem('extras', 'seats-icon')}>
    <Icon name="chevron-right" size="large" />
  </div>
)

const AncillaryControl: React.FC<{ ancillary: Ancillary.Item; disabled?: boolean }> = ({ ancillary, disabled }) => {
  const {
    values: {
      ancillaries: { SEAT: activeAncillaries },
    },
    setFieldValue,
  } = useFormikContext<CheckoutFormData>()

  const checked = activeAncillaries.includes(ancillary)
  const handleAncillaryChange = useCallback(
    (checked: boolean) => {
      setFieldValue(
        'ancillaries.SEAT',
        checked ? [...activeAncillaries, ancillary] : activeAncillaries.filter(a => a !== ancillary),
      )
    },
    [activeAncillaries, ancillary, setFieldValue],
  )

  return (
    <div className={bem('extras', 'seats-icon')} onClick={e => e.preventDefault()}>
      <Switch checked={checked} disabled={disabled} onChange={handleAncillaryChange} />
    </div>
  )
}

const Preview = ({
  onOpenModal,
  outbound,
  inbound,
  hasSeatMaps,
  ancillary,
  disabled,
  seatsLoading,
}: PreviewProps): ReactElement => {
  const { t } = useTranslation()
  const {
    values: { seats },
    errors,
  } = useFormikContext<CheckoutFormData>()
  const formError = getIn(errors, 'seats') as string | undefined
  const [couldSelectSeats, setCouldSelectSeats] = useState<boolean>()
  const {
    values: {
      ancillaries: { SEAT: activeAncillaries },
    },
    setFieldValue,
  } = useFormikContext<CheckoutFormData>()

  const selectedSeatsCount = seatsUtils.getSeatsCount(seats?.outbound) + seatsUtils.getSeatsCount(seats?.inbound)
  const isMultiSegmentSeats = seats?.outbound && Object.keys(seats?.outbound).length > 1

  const handleOpenModal: React.MouseEventHandler = (e): void => {
    if (disabled || e.defaultPrevented) return

    if (ancillary && !activeAncillaries.includes(ancillary)) {
      setFieldValue('ancillaries.SEAT', [...activeAncillaries, ancillary])
    }

    setCouldSelectSeats(onOpenModal())
  }

  const errorMessage = useMemo(() => {
    if (!hasSeatMaps && !ancillary && !disabled) return t('checkout.seats.alerts.selection_not_available')
    if (couldSelectSeats === false) return t('seats.warning.unavailable')
    if (formError) return formError

    return null
  }, [ancillary, couldSelectSeats, disabled, formError, hasSeatMaps, t])

  return (
    <Card
      dataTag="seat-selection"
      icon={seat}
      title={!isMultiSegmentSeats ? t('extras.seats.selected', { count: selectedSeatsCount }) : ''}
      description={
        <Info
          seats={seats}
          outbound={outbound}
          inbound={inbound}
          isAncillary={Boolean(ancillary)}
          hasSeatMaps={hasSeatMaps}
        />
      }
      controls={
        ancillary && !ancillaryUtils.isMandatory(ancillary) ? (
          <AncillaryControl ancillary={ancillary} disabled={disabled} />
        ) : (
          <Loader loading={!!seatsLoading} width={24} height={24}>
            {!disabled && hasSeatMaps && <Control />}
          </Loader>
        )
      }
      onClick={disabled || !hasSeatMaps ? undefined : handleOpenModal}
      warning={errorMessage && <Warning title={errorMessage} />}
      price={ancillary?.price}
    />
  )
}

export default Preview
